import React from "react";
import { NavLink, Link } from "react-router-dom";
import { Home, ShoppingBag, Gauge, User, ChevronRight, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { CC } from "./CrisClienteBrand";

export const P = {
  bg: "#F6F2FB",
  deep: "#3A1670",
  mid: "#4C2380",
  lilac: "#EFE6FA",
  lilacText: "#5A2995",
  muted: "#6F6585",
  line: "#ECE4F5",
  red: "#D32F45",
  redBg: "#FDEEF0",
  green: "#2E7D4F",
  greenBg: "#E7F5EC",
  amber: "#A86200",
  amberBg: "#FFF3E0",
};

export const PortalHeader = ({ title, subtitle, overlap = false, children }) => (
  <header className={`relative w-full rounded-b-[28px] text-white ${overlap ? "pb-24" : "pb-5"}`}
    style={{ background: "linear-gradient(160deg, #52208F 0%, #3E1874 60%, #351566 100%)", paddingTop: "calc(1rem + env(safe-area-inset-top))" }}
    data-testid="portal-header">
    <div className="mx-auto w-full max-w-2xl px-5">
      <div className="flex items-center gap-3" data-testid="portal-header-brand">
        <img src="/cliente-icons/symbol-gold.png" alt="" aria-hidden="true" className="h-10 w-auto" draggable="false" />
        <div className="leading-none">
          <div className="text-[21px] font-semibold tracking-[-0.01em]">Cris Cliente</div>
          <div className="mt-1 text-[8.5px] font-bold uppercase tracking-[0.24em] text-[#E3C99A]">Banco das Revendedoras</div>
        </div>
      </div>
      <h1 className="mt-5 text-[26px] font-extrabold leading-tight tracking-[-0.01em] sm:text-[28px]" style={{ fontFamily: CC.sans }} data-testid="portal-page-title">{title}</h1>
      {subtitle && <p className="mt-0.5 text-[15px] text-white/80">{subtitle}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  </header>
);

export const HighlightCard = ({ label, value, sub, icon: Icon, testid }) => (
  <div className="flex items-start justify-between rounded-2xl border border-white/10 bg-white/10 px-5 py-4 backdrop-blur-sm" data-testid={testid}>
    <div className="min-w-0">
      <div className="text-[14px] text-white/80">{label}</div>
      <div className="mt-0.5 truncate text-[30px] font-extrabold tabular-nums leading-tight sm:text-[32px]" data-testid={testid && `${testid}-value`}>{value}</div>
      {sub && <div className="mt-0.5 text-[13px] text-white/75">{sub}</div>}
    </div>
    {Icon && <Icon size={28} strokeWidth={1.5} className="mt-1 shrink-0 text-white/85" />}
  </div>
);

export const Card = ({ className = "", children, ...rest }) => (
  <div className={`rounded-2xl border bg-white shadow-[0_6px_20px_-14px_rgba(58,22,112,0.35)] ${className}`} style={{ borderColor: P.line }} {...rest}>
    {children}
  </div>
);

export const RowLink = ({ to, onClick, icon: Icon, title, sub, testid }) => {
  const inner = (
    <>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: P.lilac, color: P.mid }}>
        <Icon size={22} strokeWidth={1.6} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-bold" style={{ color: CC.ink }}>{title}</span>
        {sub && <span className="block truncate text-[13px]" style={{ color: P.muted }}>{sub}</span>}
      </span>
      <ChevronRight size={20} className="shrink-0" style={{ color: P.muted }} />
    </>
  );
  const cls = "flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left transition-colors hover:bg-[#FBF8FE] active:bg-[#F3ECFB]";
  const style = { borderColor: P.line };
  return to
    ? <Link to={to} className={cls} style={style} data-testid={testid}>{inner}</Link>
    : <button type="button" onClick={onClick} className={cls} style={style} data-testid={testid}>{inner}</button>;
};

export function riskTone(risk) {
  const r = (risk || "").toLowerCase();
  if (r.includes("baix") || r === "bom" || r === "otimo" || r.includes("excel")) return { fg: P.green, bg: P.greenBg, icon: CheckCircle2 };
  if (r.includes("med") || r.includes("moder")) return { fg: P.amber, bg: P.amberBg, icon: Clock };
  return { fg: P.red, bg: P.redBg, icon: AlertTriangle };
}

export function instTone(s) {
  if (s === "paga") return { fg: P.green, bg: P.greenBg, label: "Paga", icon: CheckCircle2 };
  if (s === "vencida") return { fg: P.red, bg: P.redBg, label: "Em atraso", icon: AlertTriangle };
  if (s === "parcial") return { fg: P.amber, bg: P.amberBg, label: "Parcial", icon: Clock };
  return { fg: P.lilacText, bg: P.lilac, label: "A vencer", icon: Clock };
}

export const Badge = ({ tone, children, testid, small }) => {
  const Icon = tone.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-bold ${small ? "px-2.5 py-0.5 text-[11px]" : "px-3 py-1 text-[13px]"}`}
      style={{ background: tone.bg, color: tone.fg }} data-testid={testid}>
      {Icon && <Icon size={small ? 12 : 15} strokeWidth={2.2} />} {children}
    </span>
  );
};

export const segTone = (bu) => (bu === "eletros"
  ? { bg: "#F6E9CF", fg: "#7A5617" }
  : { bg: "#EFE3FB", fg: "#5A2995" });

export const SegmentTag = ({ bu, label }) => {
  const t = segTone(bu);
  return <span className="rounded-full px-3 py-0.5 text-[12px] font-bold" style={{ background: t.bg, color: t.fg }}>{label}</span>;
};

const NAV = [
  { to: "/cliente", label: "Início", icon: Home, end: true, id: "início" },
  { to: "/cliente/compras", label: "Compras", icon: ShoppingBag, id: "compras" },
  { to: "/cliente/score", label: "Score", icon: Gauge, id: "score" },
  { to: "/cliente/conta", label: "Conta", icon: User, id: "conta" },
];

export const BottomNav = () => (
  <nav className="fixed inset-x-0 bottom-0 z-30" data-testid="portal-bottom-nav">
    <div className="mx-auto flex max-w-2xl justify-around gap-1 rounded-t-[22px] border-t bg-white/95 px-2 pt-2 backdrop-blur"
      style={{ borderColor: P.line, paddingBottom: "max(0.6rem, env(safe-area-inset-bottom))", boxShadow: "0 -8px 24px -12px rgba(58,22,112,0.25)" }}>
      {NAV.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} data-testid={`portal-nav-${it.id}`}
          className="flex flex-1 flex-col items-center gap-1 rounded-2xl py-2 text-[12px] font-semibold transition-colors"
          style={({ isActive }) => ({ color: isActive ? P.mid : "#7D7392", background: isActive ? P.lilac : "transparent" })}>
          <it.icon size={21} strokeWidth={1.8} />
          {it.label}
        </NavLink>
      ))}
    </div>
  </nav>
);

export const NAV_SPACE = "calc(6.5rem + env(safe-area-inset-bottom))";
